import { Stack, Typography } from "@mui/material";
import { StatusChip } from "components/StatusChip";
import { GetWasteShipmentQuery } from "generated-graphql/graphql";

import {
  diffFieldLabel,
  diffLimitationNote,
  formatDiffValue,
  SEVERITY_CHIP_COLORS,
  SEVERITY_LABELS,
} from "./reconciliationDisplay";

type Shipment = NonNullable<GetWasteShipmentQuery["wasteShipment"]>;
export type FieldDiff = Shipment["reconciliation"]["fieldDiffs"][number];

/**
 * How the attached manifest compares on one waste stream, rendered against the
 * stream it describes.
 *
 * This replaces a standalone diff table. The table stated a quantity for a
 * stream that Contents also stated a few inches further down, in a different
 * unit, and left the reader to correlate them. Here there is one place per
 * stream, so the comparison and the containers it came from cannot disagree.
 */
export function StreamComparison({ diffs }: { diffs: FieldDiff[] }) {
  if (diffs.length === 0) {
    return null;
  }
  const differences = diffs.filter((diff) => diff.discrepant);
  const limitations = diffs.filter((diff) => !diff.discrepant);

  return (
    <Stack spacing={0.75} sx={{ py: 1 }}>
      {differences.map((diff) => (
        <Stack
          key={`${diff.field}-${diff.wasteStreamId ?? "shipment"}`}
          direction="row"
          spacing={1.5}
          alignItems="baseline"
          flexWrap="wrap"
          useFlexGap
        >
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 108 }}>
            {diffFieldLabel(diff.field)}
          </Typography>
          {/* Each side is one unbreakable group, so a narrow screen wraps
              between "requested" and "on the manifest" rather than splitting a
              value away from the word that says which side it is. */}
          <Stack direction="row" spacing={0.75} alignItems="baseline">
            <Typography variant="body2">
              {formatDiffValue(diff.field, diff.requestValue, diff.unit)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              requested
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="baseline">
            <Typography variant="body2" sx={{ fontWeight: 600, color: "warning.main" }}>
              {formatDiffValue(diff.field, diff.manifestValue, diff.unit)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              on the manifest
            </Typography>
          </Stack>
          {diff.severity && (
            <StatusChip
              variant="soft"
              color={SEVERITY_CHIP_COLORS[diff.severity]}
              label={SEVERITY_LABELS[diff.severity]}
            />
          )}
        </Stack>
      ))}
      {limitations.map((diff) => (
        <Typography
          key={`${diff.field}-${diff.wasteStreamId ?? "shipment"}-note`}
          variant="caption"
          color="text.secondary"
        >
          {diffLimitationNote(diff.field)}
        </Typography>
      ))}
    </Stack>
  );
}

/**
 * Streams the manifest carries that this shipment never had.
 *
 * The loudest thing the comparison can say, and it has no container group to
 * attach to — the shipment does not have the stream. Without a home of its own
 * a per-stream layout would drop it silently.
 */
export function ManifestOnlyStreams({ diffs }: { diffs: FieldDiff[] }) {
  if (diffs.length === 0) {
    return null;
  }
  return (
    <Stack
      spacing={1}
      sx={{
        mt: 2.5,
        p: 2,
        borderRadius: 1,
        border: 1,
        borderColor: "error.main",
        backgroundColor: (theme) => `${theme.palette.error.main}0A`,
      }}
    >
      <Typography variant="subtitle2" color="error.main">
        On the manifest only
      </Typography>
      <Typography variant="caption" color="text.secondary">
        The manifest records waste this shipment never carried. Either the vendor combined loads, or
        the wrong manifest is attached.
      </Typography>
      {diffs.map((diff) => (
        <Stack
          key={`${diff.field}-${diff.wasteStreamId ?? "unknown"}`}
          direction="row"
          spacing={1.5}
          alignItems="baseline"
          flexWrap="wrap"
          useFlexGap
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {diff.wasteStreamName ?? "Uncharacterized"}
          </Typography>
          <Typography variant="body2">
            {formatDiffValue(diff.field, diff.manifestValue, diff.unit)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            on the manifest, not requested
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

/**
 * The comparison for waste with no stream assigned, on either side.
 *
 * It gets its own labelled block rather than sitting inside a stream group,
 * because the comparison buckets all uncharacterized waste into a single row
 * while the contents list keeps one group per uncharacterized container. There
 * is no one group to attach it to, and picking one would attribute the whole
 * bucket's variance to an arbitrary container.
 */
export function UncharacterizedComparison({ diffs }: { diffs: FieldDiff[] }) {
  if (diffs.length === 0) {
    return null;
  }
  return (
    <Stack spacing={0.5} sx={{ mb: 2 }}>
      <Typography variant="subtitle2">Uncharacterized waste</Typography>
      <Typography variant="caption" color="text.secondary">
        Compared as one bucket, because neither side names a waste stream for it.
      </Typography>
      <StreamComparison diffs={diffs} />
    </Stack>
  );
}

/**
 * Diffs about the shipment rather than any one stream — today only the notice
 * that the manifest has no reviewed source. Membership is decided by the field
 * name, because a null `wasteStreamId` means something else entirely: it is the
 * uncharacterized bucket's identity, and those rows are real comparisons.
 */
export function ShipmentLevelDiffs({ diffs }: { diffs: FieldDiff[] }) {
  if (diffs.length === 0) {
    return null;
  }
  return (
    <Stack spacing={0.5} sx={{ mb: 2 }}>
      {diffs.map((diff) => (
        <Typography key={diff.field} variant="caption" color="text.secondary">
          {diffLimitationNote(diff.field)}
        </Typography>
      ))}
    </Stack>
  );
}
